import React, { useCallback, useRef, useState } from 'react';
import { Select } from '../../Select';
import { NumberSelectProps } from '../types';
import {
  DropdownWrapper,
  NumberItem,
  NumberList,
  NumberListWrapper,
  ScrollThumb,
  ScrollTrack,
  numberSelectPopoverStyles,
  numberSelectTriggerStyles
} from './styled';

const ITEM_HEIGHT = 44;

export const NumberSelectDesktop: React.FC<NumberSelectProps> = ({
  value,
  onChange,
  max,
  placeholder,
  disabledZero,
  error,
  cypress
}) => {
  const selectRef = useRef<{ closePopover: () => void } | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(0);

  const updateThumb = useCallback(() => {
    const el = listRef.current;
    if (!el || el.scrollHeight === 0) return;
    const trackHeight = el.clientHeight - 15;
    const tHeight = Math.max(
      16,
      (el.clientHeight / el.scrollHeight) * trackHeight
    );
    const scrollable = el.scrollHeight - el.clientHeight;
    const ratio = scrollable > 0 ? el.scrollTop / scrollable : 0;
    setThumbHeight(tHeight);
    setThumbTop(ratio * (trackHeight - tHeight));
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) return;
      requestAnimationFrame(() => {
        const el = listRef.current;
        if (!el) return;
        if (value !== null) {
          const center =
            value * ITEM_HEIGHT - (el.clientHeight / 2 - ITEM_HEIGHT / 2);
          el.scrollTop = Math.max(0, center);
        }
        updateThumb();
      });
    },
    [value, updateThumb]
  );

  const handleSelect = (num: number) => {
    onChange(num);
    selectRef.current?.closePopover();
  };

  const displayValue = value !== null ? String(value) : undefined;

  return (
    <DropdownWrapper>
      <Select
        ref={selectRef}
        value={displayValue}
        placeholder={placeholder}
        styles={numberSelectTriggerStyles}
        cypress={cypress ?? ''}
        isPlaceholderVisible={!!displayValue}
        error={error}
        popoverStyles={numberSelectPopoverStyles}
        isWithHover={false}
        showIcon
        onOpenChange={handleOpenChange}
      >
        <NumberListWrapper>
          <NumberList ref={listRef} onScroll={updateThumb}>
            {Array.from({ length: max + 1 }, (_, i) => {
              const isDisabled = disabledZero && i === 0;
              const isSelected = value === i;
              return (
                <NumberItem
                  key={i}
                  $isSelected={isSelected}
                  $isDisabled={!!isDisabled}
                  onClick={() => !isDisabled && handleSelect(i)}
                >
                  {i}
                </NumberItem>
              );
            })}
          </NumberList>
          <ScrollTrack>
            <ScrollThumb $top={thumbTop} $height={thumbHeight} />
          </ScrollTrack>
        </NumberListWrapper>
      </Select>
    </DropdownWrapper>
  );
};
